/**
 * physicalExam — PURE generator: ExamData → a copy/paste-ready physical exam.
 *
 * Format matches the QME - Tool physical exam (its `examOutput.ts`), which is the
 * house style: one table per region, a section header row carrying the region's
 * section name plus the Left / Right column labels, abnormal values BOLD, normals
 * shown rather than omitted, and an "Inspection:" statement line above the table.
 * Everything is Times New Roman so it lands correctly when pasted into a report.
 *
 * Two renderers, one model — `physicalExamHtml` for the clipboard's text/html and
 * the on-screen preview, `physicalExamText` for the text/plain fallback — so what
 * you see is exactly what you paste.
 *
 * No React, no DOM: unit-testable (physicalExam.test.ts).
 */

import { NORMAL_ROM_VALUES } from './examData';
import { isMeasured } from './handCalculations';

// ── Model ────────────────────────────────────────────────────────────────────

/** One value cell. `abn` drives bolding — it means "worse than normal". */
export interface Cell {
  text: string;
  abn: boolean;
}

export interface OutputRow {
  label: string;
  left: Cell;
  right: Cell;
  italic?: boolean;
}

export interface OutputGroup {
  title: string;
  rows: OutputRow[];
}

export interface RegionOutput {
  key: string;
  title: string;
  inspection: string;
  groups: OutputGroup[];
  /** Hand: group titles are "Digit · Section" and render one table per digit. */
  digitGrouped?: boolean;
}

// ── Motion specs ─────────────────────────────────────────────────────────────

/**
 * Which direction a motion gets WORSE. 'under' = a smaller number is a deficit
 * (shoulder flexion). 'over' = a larger number is a deficit — contractures,
 * extension lags, and angular deformities, where normal is 0 or negative.
 *
 * Extension at the digits is signed (lag positive, hyperextension negative), so
 * finger MCP extension is 'over' against a normal of -20: +20 is a 40-degree
 * swing into deficit, while -25 is simply more hyperextension and stays normal.
 */
type Dir = 'under' | 'over';

interface Motion {
  /** Field suffix in ExamData, minus the right_/left_ prefix. */
  field: string;
  label: string;
  dir: Dir;
  /** Unit suffix; '°' is glued to the number, anything else is spaced. */
  unit?: string;
  /** Normal override when NORMAL_ROM_VALUES has no entry for the field. */
  normal?: number;
}

const deg = (field: string, label: string, dir: Dir = 'under'): Motion => ({ field, label, dir });

const SHOULDER: Motion[] = [
  deg('flexion', 'Flexion'),
  deg('extension', 'Extension'),
  deg('abduction', 'Abduction'),
  deg('adduction', 'Adduction'),
  deg('internal_rotation', 'Internal rotation'),
  deg('external_rotation', 'External rotation'),
];

const ELBOW: Motion[] = [
  deg('flexion', 'Flexion'),
  // Normal elbow extension is 0; anything above 0 is a flexion contracture.
  deg('extension', 'Extension (lag)', 'over'),
  deg('pronation', 'Pronation'),
  deg('supination', 'Supination'),
];

const WRIST: Motion[] = [
  deg('flexion', 'Flexion'),
  deg('extension', 'Extension'),
  deg('radial_deviation', 'Radial deviation'),
  deg('ulnar_deviation', 'Ulnar deviation'),
];

const HIP: Motion[] = [
  deg('flexion', 'Flexion'),
  deg('extension_contracture', 'Extension contracture', 'over'),
  deg('internal_rotation', 'Internal rotation'),
  deg('external_rotation', 'External rotation'),
  deg('adduction', 'Adduction'),
  deg('abduction_contracture', 'Abduction contracture', 'over'),
];

const KNEE: Motion[] = [
  deg('flexion', 'Flexion'),
  deg('flexion_contracture_passive', 'Flexion contracture (passive)', 'over'),
  deg('extensor_lag', 'Extensor lag', 'over'),
  deg('varus', 'Varus', 'over'),
  deg('valgus', 'Valgus', 'over'),
];

const ANKLE: Motion[] = [
  deg('plantar_flexion', 'Plantar flexion'),
  deg('dorsiflexion', 'Dorsiflexion'),
  deg('inversion', 'Inversion'),
  deg('eversion', 'Eversion'),
];

const THUMB_ROM: Motion[] = [
  deg('ip_flexion', 'IP flexion'),
  deg('ip_extension', 'IP extension', 'over'),
  deg('mcp_flexion', 'MCP flexion'),
  deg('mcp_extension', 'MCP extension', 'over'),
  deg('cmc_radial_abduction', 'CMC radial abduction'),
  { field: 'cmc_adduction', label: 'CMC adduction', dir: 'over', unit: 'cm' },
  { field: 'cmc_opposition', label: 'CMC opposition', dir: 'under', unit: 'cm' },
];

const FINGER_ROM: Motion[] = [
  deg('dip_flexion', 'DIP flexion'),
  deg('dip_extension', 'DIP extension', 'over'),
  deg('pip_flexion', 'PIP flexion'),
  deg('pip_extension', 'PIP extension', 'over'),
  deg('mcp_flexion', 'MCP flexion'),
  deg('mcp_extension', 'MCP extension', 'over'),
];

const FINGERS: { key: string; label: string }[] = [
  { key: 'index_fingers', label: 'Index finger' },
  { key: 'middle_fingers', label: 'Middle finger' },
  { key: 'ring_fingers', label: 'Ring finger' },
  { key: 'little_fingers', label: 'Little finger' },
];

// ── Cell helpers ─────────────────────────────────────────────────────────────

const NOT_RECORDED = 'Not recorded';

const cell = (text: string, abn: boolean): Cell => ({ text, abn });

const fmtVal = (n: number, unit: string) => (unit === '°' ? `${n}°` : `${n} ${unit}`);

/** Look up a motion's normal, preferring the shared NORMAL_ROM_VALUES table. */
function normalFor(region: string, m: Motion, side: 'right' | 'left'): number | null {
  if (m.normal !== undefined) return m.normal;
  const table = NORMAL_ROM_VALUES[region];
  const v = table?.[`${side}_${m.field}`];
  return typeof v === 'number' ? v : null;
}

/**
 * A value cell for one motion on one side. A missing value reads "Not recorded"
 * and is never bolded — an unexamined motion is not an abnormal one.
 */
function motionCell(
  value: unknown,
  normal: number | null,
  dir: Dir,
  unit = '°',
): Cell {
  // Same test the rating uses, so the exam and the rating can never disagree
  // about whether a measurement exists (a parser returning "70" was rated 7%
  // while this section dropped the region entirely).
  if (!isMeasured(value)) return cell(NOT_RECORDED, false);
  const n = Number(value);
  const abn = normal === null ? false : dir === 'under' ? n < normal : n > normal;
  return cell(fmtVal(n, unit), abn);
}

/** Severity/finding cell: anything but "None" is abnormal. */
function severityCell(value: unknown): Cell {
  const s = typeof value === 'string' && value.trim() ? value.trim() : null;
  if (!s) return cell(NOT_RECORDED, false);
  return cell(s, s.toLowerCase() !== 'none');
}

/**
 * Two-point discrimination: normal is 5 mm; above 6 mm is a sensory deficit.
 * The digit-length percent only matters for a deficit, so a normal cell reads
 * just "5 mm". OneNote sizes columns to their longest text and ignores declared
 * widths, so a "(100%)" on every normal finger widened both Left/Right columns.
 */
function twoPointCell(mm: unknown, pct: unknown): Cell {
  if (!isMeasured(mm)) return cell(NOT_RECORDED, false);
  const n = Number(mm);
  const abn = n > 6;
  const pctText = abn && isMeasured(pct) ? ` (${Number(pct)}%)` : '';
  return cell(`${n} mm${pctText}`, abn);
}

function percentCell(value: unknown): Cell {
  if (!isMeasured(value)) return cell(NOT_RECORDED, false);
  const n = Number(value);
  return cell(`${n}%`, n > 0);
}

/** True when at least one cell in the group carries a recorded value. */
const hasData = (rows: OutputRow[]): boolean =>
  rows.some((r) => r.left.text !== NOT_RECORDED || r.right.text !== NOT_RECORDED);

// ── Region builders ──────────────────────────────────────────────────────────

function romRows(region: string, data: any, motions: Motion[]): OutputRow[] {
  return motions.map((m) => {
    const unit = m.unit ?? '°';
    const rNormal = normalFor(region, m, 'right');
    const lNormal = normalFor(region, m, 'left');
    const normalText =
      rNormal !== null ? ` (Nl ${fmtVal(rNormal, unit)})` : '';
    return {
      label: `${m.label}${normalText}`,
      right: motionCell(data?.[`right_${m.field}`], rNormal, m.dir, unit),
      left: motionCell(data?.[`left_${m.field}`], lNormal, m.dir, unit),
    };
  });
}

function bilateralRegion(
  key: string,
  title: string,
  inspection: string,
  data: any,
  motions: Motion[],
): RegionOutput | null {
  const rows = romRows(key, data, motions);
  if (!hasData(rows)) return null;
  return { key, title, inspection, groups: [{ title: 'Range of motion', rows }] };
}

function handRegion(hands: any): RegionOutput | null {
  if (!hands) return null;
  const groups: OutputGroup[] = [];

  const digitGroups = (
    digitLabel: string,
    data: any,
    motions: Motion[],
    normalsKey: string,
    extras: OutputRow[],
  ) => {
    const rom = romRows(normalsKey, data, motions);
    const sensory: OutputRow[] = [
      {
        label: 'Radial two-point (Nl 5 mm)',
        right: twoPointCell(data?.right_radial_two_point, data?.right_radial_two_point_percent),
        left: twoPointCell(data?.left_radial_two_point, data?.left_radial_two_point_percent),
      },
      {
        label: 'Ulnar two-point (Nl 5 mm)',
        right: twoPointCell(data?.right_ulnar_two_point, data?.right_ulnar_two_point_percent),
        left: twoPointCell(data?.left_ulnar_two_point, data?.left_ulnar_two_point_percent),
      },
    ];
    if (!hasData(rom) && !hasData(sensory) && !hasData(extras)) return;
    groups.push({ title: `${digitLabel} · Range of motion`, rows: rom });
    // "Sensation (2PD)", not "Two-point discrimination": it is the header's width
    // that OneNote sizes the label column to.
    groups.push({ title: `${digitLabel} · Sensation (2PD)`, rows: sensory });
    groups.push({ title: `${digitLabel} · Other findings`, rows: extras });
  };

  const thumbs = hands.thumbs;
  digitGroups('Thumb', thumbs, THUMB_ROM, 'thumbs', [
    {
      label: 'Triggering',
      right: severityCell(thumbs?.right_triggering),
      left: severityCell(thumbs?.left_triggering),
    },
    {
      label: 'Amputation',
      right: percentCell(thumbs?.right_amputation_percent),
      left: percentCell(thumbs?.left_amputation_percent),
    },
  ]);

  for (const { key, label } of FINGERS) {
    const f = hands[key];
    digitGroups(label, f, FINGER_ROM, key, [
      {
        label: 'Triggering',
        right: severityCell(f?.right_triggering),
        left: severityCell(f?.left_triggering),
      },
      {
        label: 'Extensor subluxation at MCP',
        right: severityCell(f?.right_extensor_subluxation_mcp),
        left: severityCell(f?.left_extensor_subluxation_mcp),
      },
      {
        label: 'Amputation',
        right: percentCell(f?.right_amputation_percent),
        left: percentCell(f?.left_amputation_percent),
      },
    ]);
  }

  if (!groups.length) return null;
  return {
    key: 'hands',
    title: 'Hands',
    inspection:
      'Skin intact without scars, atrophy, or deformity unless noted. ' +
      'Extension is recorded signed: a lag is positive, hyperextension negative.',
    groups,
    digitGrouped: true,
  };
}

function gripRegion(grip: any): RegionOutput | null {
  if (!grip) return null;
  const attempt = (n: 1 | 2 | 3): OutputRow => ({
    label: `Attempt ${n} (kg)`,
    right: isMeasured(grip[`right_attempt_${n}`])
      ? cell(`${Number(grip[`right_attempt_${n}`])}`, false)
      : cell(NOT_RECORDED, false),
    left: isMeasured(grip[`left_attempt_${n}`])
      ? cell(`${Number(grip[`left_attempt_${n}`])}`, false)
      : cell(NOT_RECORDED, false),
  });
  const rows = [attempt(1), attempt(2), attempt(3)];
  if (!hasData(rows)) return null;

  const avg = (side: 'right' | 'left') => {
    const vals = [1, 2, 3]
      .map((n) => grip[`${side}_attempt_${n}`])
      .filter(isMeasured).map(Number);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const rAvg = avg('right');
  const lAvg = avg('left');
  // Bold the weaker side's average once its loss against the other hand reaches
  // 10%, where Table 16-34 starts rating (every grip cell used to be plain, even
  // for a 30% UE strength loss).
  const weakAbn = (mine: number | null, other: number | null) =>
    mine !== null && other !== null && other > 0 && (other - mine) / other >= 0.1;
  rows.push({
    label: 'Average',
    right: rAvg === null ? cell(NOT_RECORDED, false) : cell(`${rAvg.toFixed(1)} kg`, weakAbn(rAvg, lAvg)),
    left: lAvg === null ? cell(NOT_RECORDED, false) : cell(`${lAvg.toFixed(1)} kg`, weakAbn(lAvg, rAvg)),
  });

  return {
    key: 'grip_strength',
    title: 'Grip strength',
    inspection: 'Jamar dynamometer, position II, three trials per hand.',
    groups: [{ title: 'Grip strength', rows }],
  };
}

/** Build every region that has at least one recorded value, in report order. */
export function buildPhysicalExam(examData: any): RegionOutput[] {
  if (!examData) return [];
  const out: (RegionOutput | null)[] = [
    bilateralRegion(
      'shoulders', 'Shoulders',
      'No deformity, atrophy, or scars. Nontender unless noted.',
      examData.shoulders, SHOULDER,
    ),
    bilateralRegion(
      'elbows', 'Elbows',
      'No effusion, deformity, or scars. Nontender unless noted.',
      examData.elbows, ELBOW,
    ),
    bilateralRegion(
      'wrists', 'Wrists',
      'No swelling, deformity, or scars. Nontender unless noted.',
      examData.wrists, WRIST,
    ),
    handRegion(examData.hands),
    gripRegion(examData.grip_strength),
    bilateralRegion(
      'hips', 'Hips',
      'No deformity or scars. Nontender unless noted.',
      examData.hips, HIP,
    ),
    bilateralRegion(
      'knees', 'Knees',
      'No effusion, deformity, or scars. Nontender unless noted.',
      examData.knees, KNEE,
    ),
    bilateralRegion(
      'ankles', 'Ankles',
      'No swelling, deformity, or scars. Nontender unless noted.',
      examData.ankles, ANKLE,
    ),
  ];
  // A row with nothing recorded on EITHER side says nothing and made the pasted
  // exam mostly "Not recorded" (e.g. an unexamined CMC opposition row). Drop
  // those rows, and any section left empty. A row recorded on one side still
  // shows "Not recorded" for the other, so what was not examined stays visible.
  const recorded = (r: OutputRow) => r.left.text !== NOT_RECORDED || r.right.text !== NOT_RECORDED;
  return out
    .filter((r): r is RegionOutput => r !== null)
    .map((o) => ({
      ...o,
      groups: o.groups
        .map((g) => ({ ...g, rows: g.rows.filter(recorded) }))
        .filter((g) => g.rows.length > 0),
    }));
}

// ── Renderers ────────────────────────────────────────────────────────────────

const FONT = "font-family:'Times New Roman',serif;";
// Long labels must WRAP rather than widen their column — without this a row like
// "Flexion contracture (passive) (Nl 0°)" pushes the table past the page margin.
const BORDER = 'border:1px solid #888;padding:4px 8px;word-wrap:break-word;overflow-wrap:break-word;';
const SHD_SECTION = 'f2f2f2';

/**
 * Column widths. `table-layout:fixed` plus an explicit colgroup is what keeps the
 * table inside the page when pasted into Word: left to itself Word sizes columns
 * to their content and lets a wide table bleed off the right margin. The <col>
 * percentages and the per-cell width attributes say the same thing twice, because
 * Word's HTML import honours the attribute more reliably than the CSS.
 */
const COLS_BILATERAL = [50, 25, 25];

const colgroup = (widths: number[]) =>
  `<colgroup>${widths.map((w) => `<col style="width:${w}%" width="${w}%">`).join('')}</colgroup>`;

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Formatting is baked in SEMANTICALLY (<strong>/<em>, not CSS) so it survives a
// paste into Word or a rich-text editor.
function fmt(text: string, opts: { bold?: boolean; italic?: boolean }): string {
  let s = esc(text);
  if (opts.bold) s = `<strong>${s}</strong>`;
  if (opts.italic) s = `<em>${s}</em>`;
  return s;
}

// Alignment rides on a <p> inside the cell: editors that re-parse the HTML read
// alignment from block elements, not from a <td style="text-align:center">.
function cellP(inner: string, center: boolean): string {
  return `<p style="margin:0;${center ? 'text-align:center;' : ''}">${inner}</p>`;
}

function cellHtml(c: Cell, italic: boolean): string {
  return `<td style="${BORDER}text-align:center;">${cellP(fmt(c.text, { bold: c.abn, italic }), true)}</td>`;
}

function rowHtml(r: OutputRow): string {
  const it = !!r.italic;
  return (
    `<tr><td style="${BORDER}">${cellP(fmt(r.label, { italic: it }), false)}</td>` +
    `${cellHtml(r.left, it)}${cellHtml(r.right, it)}</tr>`
  );
}

export function regionExamHtml(o: RegionOutput): string {
  const w = COLS_BILATERAL;
  // Every section header repeats the column widths, so Word sizes the table from
  // the first row it meets rather than from the widest label further down.
  const hdrCell = (label: string, center: boolean, width: number) =>
    `<td data-shd="${SHD_SECTION}" width="${width}%" style="${BORDER}width:${width}%;background:#f2f2f2;${center ? 'text-align:center;' : ''}">` +
    `${cellP(`<strong>${esc(label)}</strong>`, center)}</td>`;
  // The Left/Right labels repeat on every section header so a reader deep in a
  // long table never has to scroll back up to tell the columns apart.
  const sectionRow = (title: string) =>
    `<tr>${hdrCell(title.toUpperCase(), false, w[0])}${hdrCell('Left', true, w[1])}${hdrCell('Right', true, w[2])}</tr>`;
  const tableOf = (groups: OutputGroup[], sectionTitle: (t: string) => string) =>
    `<table width="100%" style="border-collapse:collapse;width:100%;table-layout:fixed;${FONT}">` +
    colgroup(w) +
    `<tbody>` +
    groups
      .map((g) => sectionRow(sectionTitle(g.title)) + g.rows.map(rowHtml).join(''))
      .join('') +
    `</tbody></table>`;

  const head =
    `<p style="${FONT}"><strong>${esc(o.title.toUpperCase())}</strong></p>` +
    (o.inspection.trim() ? `<p style="${FONT}"><strong>Inspection:</strong> ${esc(o.inspection)}</p>` : '');

  if (!o.groups.length) return head;

  // Hand: group titles are "Digit · Section" — one table per digit, each under
  // its own sub-heading, tables separated by a blank paragraph.
  if (o.digitGrouped) {
    const order: string[] = [];
    const byDigit = new Map<string, OutputGroup[]>();
    for (const g of o.groups) {
      const digit = g.title.split('·')[0].trim();
      if (!byDigit.has(digit)) { byDigit.set(digit, []); order.push(digit); }
      byDigit.get(digit)!.push(g);
    }
    const sectionOnly = (t: string) => t.split('·').slice(1).join('·').trim() || t;
    const blocks = order.map(
      (digit) =>
        `<p style="${FONT}"><strong>${esc(digit.toUpperCase())}</strong></p>` +
        tableOf(byDigit.get(digit)!, sectionOnly),
    );
    return head + blocks.join(`<p style="${FONT}">&nbsp;</p>`);
  }

  return head + tableOf(o.groups, (t) => t);
}

export function physicalExamHtml(outputs: RegionOutput[]): string {
  return outputs.map(regionExamHtml).join(`<p style="${FONT}">&nbsp;</p>`);
}

/** Plain-text fallback for the clipboard's text/plain. */
export function physicalExamText(outputs: RegionOutput[]): string {
  return outputs
    .map((o) => {
      const lines: string[] = [o.title.toUpperCase()];
      if (o.inspection.trim()) lines.push(`Inspection: ${o.inspection}`);
      for (const g of o.groups) {
        lines.push('', g.title.toUpperCase());
        for (const r of g.rows) {
          lines.push(
            `  ${r.label}: L: ${r.left.text} | R: ${r.right.text}`,
          );
        }
      }
      return lines.join('\n');
    })
    .join('\n\n');
}
